import { useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { AlertTriangle, CheckCircle2, Flame, Info, Wrench, X } from 'lucide-react'
import { useBannerStore, type ResolvedBanner } from '../../store/bannerStore'
import { SEVERITY_STYLES } from '../../config/banners'
import { resolveLocalizedString } from '../../lib/localizedString'
import { withLangPrefix } from '../../lib/withLangPrefix'
import type { BannerLink } from '../../lib/runtimeConfig'

const ICONS = {
  info: Info,
  check: CheckCircle2,
  alert: AlertTriangle,
  flame: Flame,
  wrench: Wrench,
} as const

const BANNER_HEIGHT_VAR = '--banner-stack-height'
const MARQUEE_INTERVAL_MS = 4000

// Strip the lang prefix so banner targeting matches '/shop' not '/en/shop'.
function stripLang(pathname: string): string {
  return pathname.replace(/^\/[a-z]{2}(?=\/|$)/, '') || '/'
}

export default function NotificationBanners() {
  const location = useLocation()
  const { i18n, t } = useTranslation()
  const path = stripLang(location.pathname)
  const banners = useBannerStore((s) => s.banners)
  const dismissed = useBannerStore((s) => s.dismissed)
  const dismiss = useBannerStore((s) => s.dismiss)
  const visibleOn = useBannerStore((s) => s.visibleOn)
  // banners/dismissed are intentional invalidation deps — visibleOn closes over store state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const visible = useMemo(() => visibleOn(path), [visibleOn, banners, dismissed, path])

  const containerRef = useRef<HTMLDivElement>(null)

  // Measure rendered height and expose it as a CSS var so the navbar can shift
  // down by exactly that amount. Re-measure on resize and on banner stack change.
  useEffect(() => {
    const root = document.documentElement
    if (visible.length === 0) {
      root.style.setProperty(BANNER_HEIGHT_VAR, '0px')
      return
    }
    const el = containerRef.current
    if (!el) return

    const update = () => {
      root.style.setProperty(BANNER_HEIGHT_VAR, `${el.offsetHeight}px`)
    }
    update()

    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => {
      ro.disconnect()
      root.style.setProperty(BANNER_HEIGHT_VAR, '0px')
    }
  }, [visible.length])

  if (visible.length === 0) return null

  const marquee = visible.filter((b) => b.display === 'marquee-vertical')
  const standard = visible.filter((b) => b.display !== 'marquee-vertical')

  return (
    <div
      ref={containerRef}
      className="fixed top-0 left-0 right-0 z-[60]"
      role="region"
      aria-label="Site notifications"
    >
      {marquee.length > 0 && (
        <MarqueeRow banners={marquee} lang={i18n.language} />
      )}
      <AnimatePresence initial={false}>
        {standard.map((banner) => {
          const message = resolveLocalizedString(banner.message, i18n.language)
          const label = banner.label
            ? resolveLocalizedString(banner.label, i18n.language)
            : t(`system.banner.labels.${banner.severity}`)
          return (
            <BannerRow
              key={banner.id}
              banner={banner}
              message={message}
              label={label}
              dismissLabel={t('system.banner.dismiss')}
              onDismiss={() => dismiss(banner.id)}
              lang={i18n.language}
            />
          )
        })}
      </AnimatePresence>
    </div>
  )
}

interface BannerRowProps {
  banner: ResolvedBanner
  message: string
  label: string
  dismissLabel: string
  onDismiss: () => void
  lang: string
}

function BannerRow({ banner, message, label, dismissLabel, onDismiss, lang }: BannerRowProps) {
  const style = SEVERITY_STYLES[banner.severity]
  const Icon = ICONS[style.icon]
  return (
    <motion.div
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -40, opacity: 0 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className={`${style.bg} ${style.text} border-b ${style.border}`}
      role={banner.severity === 'critical' || banner.severity === 'maintenance' ? 'alert' : 'status'}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-3">
        <Icon size={18} aria-hidden="true" className="flex-shrink-0" />
        <p className="font-body text-sm flex-1">
          <span className="font-semibold">{label}</span>{' '}
          <MessageWithLink message={message} link={banner.link} lang={lang} />
        </p>
        {banner.dismissible && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label={dismissLabel}
            className="p-1 -m-1 hover:opacity-70 transition-opacity flex-shrink-0"
          >
            <X size={16} />
          </button>
        )}
      </div>
    </motion.div>
  )
}

/**
 * Single brand-mocha row that rotates through one promo at a time, always
 * animating upward (out the top, in from the bottom). With a single banner
 * the same message loops, so the upward motion is always visible.
 *
 * No icons, no dismiss — promotional ticker, not an alert.
 * Pauses on hover and respects prefers-reduced-motion (static, no rotation).
 */
function MarqueeRow({ banners, lang }: { banners: ResolvedBanner[]; lang: string }) {
  const [tick, setTick] = useState(0)
  const [paused, setPaused] = useState(false)
  const reduceMotion = useReducedMotion()

  useEffect(() => {
    if (banners.length === 0 || paused || reduceMotion) return
    const t = window.setInterval(() => {
      setTick((n) => n + 1)
    }, MARQUEE_INTERVAL_MS)
    return () => window.clearInterval(t)
  }, [banners.length, paused, reduceMotion])

  if (banners.length === 0) return null

  const safeIndex = tick % banners.length
  const current = banners[safeIndex]
  const message = resolveLocalizedString(current.message, lang)

  return (
    <div
      className="bg-mocha text-ivory border-b border-gold/30 overflow-hidden"
      role="status"
      aria-live="polite"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-9 py-1.5 sm:py-0 flex items-center justify-center">
        <div className="relative w-full flex items-center justify-center">
          <AnimatePresence mode="wait" initial={false}>
            <motion.p
              key={`${current.id}:${tick}`}
              initial={reduceMotion ? false : { y: 18, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={reduceMotion ? { opacity: 0 } : { y: -18, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="font-body text-sm text-center"
            >
              <MessageWithLink
                message={message}
                link={current.link}
                lang={lang}
                linkClassName="underline decoration-gold decoration-1 underline-offset-4 hover:text-gold transition-colors"
              />
            </motion.p>
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}

/**
 * Renders a banner message with an optional inline link. If the message
 * contains the literal token `{link}`, that token is replaced with the
 * link. Otherwise the link is appended to the end of the message.
 */
function MessageWithLink({
  message,
  link,
  lang,
  linkClassName,
}: {
  message: string
  link?: BannerLink
  lang: string
  linkClassName?: string
}) {
  if (!link) return <>{message}</>
  const linkText = resolveLocalizedString(link.text, lang)
  const isAbsolute = /^https?:\/\//i.test(link.href)
  const external = link.external ?? isAbsolute
  const anchorClass = linkClassName ?? 'underline decoration-gold underline-offset-2 hover:text-gold transition-colors'

  // External URLs open in a new tab via raw <a>. Internal hrefs use
  // RouterLink so navigation stays inside the SPA (no full page reload)
  // AND the current lang prefix is preserved - `/membership` from
  // runtime config becomes `/{lang}/membership`. Without this, react-
  // router's `/:lang/membership` route doesn't match and the user falls
  // through to the home route catch-all.
  const anchor = external ? (
    <a
      href={link.href}
      className={anchorClass}
      target="_blank"
      rel="noopener noreferrer"
    >
      {linkText}
    </a>
  ) : (
    <RouterLink to={withLangPrefix(link.href, lang)} className={anchorClass}>
      {linkText}
    </RouterLink>
  )

  const TOKEN = '{link}'
  const idx = message.indexOf(TOKEN)
  if (idx === -1) {
    return (
      <>
        {message} {anchor}
      </>
    )
  }
  const before = message.slice(0, idx)
  const after = message.slice(idx + TOKEN.length)
  return (
    <>
      {before}
      {anchor}
      {after}
    </>
  )
}
